import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {Col, Row, Nav, Tab, Card, Dropdown, Badge, Table, Collapse} from 'react-bootstrap';
import {BaseModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import {EVENT_TYPE_CHAT, EVENT_TYPE_EMAIL, EVENT_TYPE_FEEDBACK} from 'utils/eventConstants';
import Icon from '@/components/icons/Icon';
import axios from 'axios';
import CellLoader from '@/components/loading/CellLoader';
import useData from '@/utils/useData';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {buildURL, buildEventURL} from '@/utils/urlBuilder';
import AddNoteForm from './AddNoteForm';
import {useAlert} from '@/components/alerts';
import {navigate} from '@reach/router';
import EventLangChangeForm from './EventLangChangeForm';
import AuditTab from './AuditTab';
import EventHeader from './EventHeader';
import {EventActorsList} from './EventActorsList';
import ChatDetailBlock from './ChatDetailBlock';
import EmailDetailBlock from './EmailDetailBlock';
import FeedbackDetailBlock from './FeedbackDetailBlock';
import {Tooltip} from '@/components/tooltips';
import EventEvaluationsTab from './EventEvaluationsTab';
import EventSummary from './EventSummary';
import {getEventEvaluationUrl} from './utils/evaluationsUrl';
import i18n from 'i18n';
import Button from '@/components/ui/Button';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import EventAgentCustomerNameBlock from './EventAgentCustomerNameBlock';
import RenderNoDataAvailable, {RenderFieldValue} from 'components/utils/DataNotAvailable';
import {useUrls} from '@/hooks/useConfig';
import {useStoreState} from 'easy-peasy';
import {UserDetails} from '@/entities/user';
import {extractErrorMessage} from '@/utils/errorHandling';
import {
    EventDetails,
    EventIndexDetails,
    EventComment,
    LangFieldChoice,
    Evaluation,
    ModuleData,
    AppConfig,
    EventDetailBlockWithoutPlayerProps
} from '../../types/event';
import {hasPermission} from '@/utils/permissions/permissionDebug';

const REPLACE_ORIGINAL_ACTION = 'replace_original';
import {isModuleEnabled} from '@/utils/modules';

interface UserData extends UserDetails {
    [key: string]: any;
}

const EventDetailBlockWithoutPlayer = React.memo(
    (
        {
            activeEventId,
            eventDetails,
            loading,
        }: EventDetailBlockWithoutPlayerProps,
    ) => {
        const {showSuccess, showError} = useAlert();
        const {urls} = useUrls();
        const appConfig: AppConfig = useStoreState((state: any) => state.appConfigs.items);
        const menuStructure = useStoreState((state: any) => state.sidebarMenu.menuStructure);

        const [changeLangModalOpen, setChangeLangModalOpen] = useState(false);
        const [commentModalOpen, setCommentModalOpen] = useState(false);
        const [selectedComment, setSelectedComment] = useState<EventComment | null>(null);
        const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
        const {userData, loading: loadingUserData} = useOptimizedConfig();
        const [extIdCopySuccess, setExtIdCopySuccess] = useState(false);
        const [activeTab, setActiveTab] = useState('call-details');
        const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
        const [confirmAction, setConfirmAction] = useState<{
            message: string;
            url: string;
            functionName: string;
            displayName: string;
        } | null>(null);
        const [isExecuting, setIsExecuting] = useState<boolean>(false);
        const [isEvaluationsTabClicked, setIsEvaluationsTabClicked] = useState(false);
        const [isAuditTabClicked, setIsAuditTabClicked] = useState(false);
        const [isNotesTabClicked, setIsNotesTabClicked] = useState(false);
        const [data, setData] = useState<{[key: string]: ModuleData} | null>(null);
        const [actionModalOpen, setActionModalOpen] = useState(false);
        const [actionModalContent, setActionModalContent] = useState('');
        const [actionModalTitle, setActionModalTitle] = useState('');
        const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
        const [loadingEvaluations, setLoadingEvaluations] = useState(false);

        const canAccessAudit = hasPermission('fs.event.audit_trail.read', userData?.permissions, true);

        const getEventEvaluationsPayload = () =>
            axios.get(buildEventURL('eventEvaluationsPayload', activeEventId));

        const [eventIndexDetails, loadingEventIndexDetails] = useData<EventIndexDetails>(
            buildEventURL('eventIndexDataDetails', activeEventId), 'eventDetail', 'setEventIndexDetails', 'eventIndexDetails',
        );

        const [eventComments, loadingEventComments] = useData<EventComment[]>(
            isNotesTabClicked ? buildEventURL('eventComments', activeEventId) : null, 'eventDetail', 'setEventComments', 'eventComments',
        );

        const [langFieldChoices, loadingLangChoices] = useData<LangFieldChoice[]>(
            !loading && eventDetails?.eventType?.id ? buildURL('langFieldChoices', {obj_id: eventDetails.eventType.id}) : null, 'langFieldChoices'
        );


        useEffect(() => {
            if (loadingUserData) {return;}
            const fetchData = async () => {
                try {
                    const response = await getEventEvaluationsPayload();
                    if (eventDetails?.eventTypeName) {
                        setData(response.data[eventDetails.eventTypeName]);
                    }
                } catch (err) {
                    console.error('Error fetching data:', err instanceof Error ? err.message : 'Unknown error');
                }
            };

            fetchData();
        }, [activeEventId, eventDetails?.eventTypeName, loadingUserData]);



        const fetchEvaluations = useCallback(async () => {
            setLoadingEvaluations(true);
            try {
                const response = await axios.get(getEventEvaluationUrl(activeEventId));
                if (response.data) {
                    const filteredData = response.data.filter(
                        (item: Evaluation) => item.type !== 'summary_text_sample'
                    );
                    setEvaluations(filteredData.sort((a: Evaluation, b: Evaluation) => b.priority - a.priority));
                } else {
                    console.error('API response data is undefined');
                }
            } catch (error) {
                console.error('Error fetching evaluations:', error);
            } finally {
                setLoadingEvaluations(false);
            }
        }, [activeEventId]);

        useEffect(() => {
            fetchEvaluations();
        }, [activeEventId]);

        if (!data) {
            return <CellLoader fullHeight={true} />;
        }

        const {eventActors} = eventDetails as EventDetails;
        const detailRestrictedFields: string[] = [
            ...(userData?.restrictedFieldKeys || []),
            ...(eventDetails?._restricted_fields || []),
            ...(eventIndexDetails?._restricted_fields || []),
        ];
        const {eventAgent, eventCustomer} = EventActorsList(eventDetails, eventIndexDetails, userData, detailRestrictedFields);

        const closeCommentModal = () => {
            setCommentModalOpen(false);
        };

        const closeSettingsModal = () => {
            setChangeLangModalOpen(false);
            showSuccess(i18n.t('jt_language_updated_successfully.text'));
        };

        const handleTabSelect = (key: string) => {
            setActiveTab(key);
            if (key === 'evaluations') {
                setIsEvaluationsTabClicked(true);
            } else if (key === 'audit') {
                setIsAuditTabClicked(true);
            } else if (key === 'notes') {
                setIsNotesTabClicked(true);
            }
        };

        const handleTabChange = (tabName: string) => {
            setActiveTab(tabName);
        };

        const copyExternalId = (extId: string) => {
            navigator.clipboard.writeText(extId).then(() => {
                setExtIdCopySuccess(true);
                setTimeout(() => setExtIdCopySuccess(false), 2000);
            }).catch(err => {
            });
        }




        const getInfoTitle = (eventTypeName: string) => {
            switch (eventTypeName) {
                case EVENT_TYPE_FEEDBACK:
                    return i18n.t('jt_feedback_information.text');
                case EVENT_TYPE_CHAT:
                    return i18n.t('jt_chat_information.text');
                case EVENT_TYPE_EMAIL:
                    return i18n.t('jt_email_information.text');
                default:
                    return '';
            }
        };

        // Event Summary
        let eventSummaryDisplay;
        if (eventDetails) {
            const detailsBox = data?.['details-box'] as any;
            const automaticSummary =
                detailsBox?.['addon-automatic-summary'] || detailsBox?.['automatic-summary'];
            const eventPreview = detailsBox?.['event-preview'];

            const summaryToDisplay =
                automaticSummary?.keys?.some((keyItem: any) => keyItem.value)
                    ? automaticSummary
                    : eventPreview || automaticSummary || data['addon-automatic-summary'] || data['automatic-summary'];

            eventSummaryDisplay = (
                <EventSummary
                    eventDetails={eventDetails}
                    summaryToDisplay={summaryToDisplay}
                    userData={userData}
                    {...(summaryToDisplay === eventPreview ? {setActiveTab: handleTabChange} : {})}
                />
            );
        } else {
            eventSummaryDisplay = RenderNoDataAvailable();
        }


        // Event info card
        let eventInfoDisplay;
        if (eventDetails) {
            eventInfoDisplay = (
                <Col md={6}>
                    <Card className="mb-0 custom-border">
                        <div className="overflow-auto w-100">
                            <Table className="event-detail-table">
                                <thead>
                                    <tr>
                                        <th colSpan={2}
                                            className="align-content-center">{getInfoTitle(eventDetails.eventTypeName)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_event.text')}</h5>
                                            {eventDetails?.id ? (
                                                <span className="d-block event-detail-text-sm">{eventDetails.id}</span>
                                            ) : RenderNoDataAvailable()}
                                        </td>
                                        <td data-cy="external-id-container">
                                            <h5 className="d-block event-detail-external-id-header">{i18n.t('jt_external.text')}</h5>
                                            {eventDetails?.externalId ? (
                                                <div className="d-flex align-items-center event-detail-text-sm">
                                                    <span className="text-wrap">
                                                        {eventDetails.externalId}
                                                    </span>
                                                    <span
                                                        className="text-secondary ml-1 event-detail-card-clickable"
                                                        onClick={() => copyExternalId(eventDetails.externalId as string)}
                                                        data-cy="copy-externalid-btn"
                                                        aria-label="Copy External Id"
                                                    >
                                                        {extIdCopySuccess ? (
                                                            <Icon name="mdi-check" size="xs" className="text-info" />
                                                        ) : (
                                                            <Icon name="mdi-content-copy" size="xs" />
                                                        )}
                                                    </span>
                                                </div>
                                            ) : i18n.t('event_not_avb.text')}
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_datetime.text')}</h5>
                                            {eventDetails?.datetime ? (
                                                <div className="d-block event-detail-text-sm">
                                                    <FormattedDateTime
                                                        dateTime={eventDetails.datetime}
                                                        showTime={false}
                                                        options={{zone: 'utc'}}
                                                    />
                                                </div>
                                            ) : RenderNoDataAvailable()}
                                        </td>

                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_language.text')}</h5>
                                            <div className="d-flex align-items-center event-detail-text-sm">
                                                <>
                                                    <span className="text-nowrap">
                                                        {langFieldChoices.find((x) => x.value === eventDetails.lang)?.label || eventDetails.lang || 'Not selected'}
                                                    </span>
                                                    <span
                                                        className="text-secondary ml-1 event-detail-card-clickable"
                                                        onClick={() => setChangeLangModalOpen(true)}
                                                        data-cy="language-change-btn"
                                                        aria-label="Change Language"
                                                    >
                                                        <Icon name="mdi-pencil-outline" size="xs" />
                                                    </span>
                                                </>
                                            </div>
                                        </td>
                                    </tr>

                                    <tr>
                                        <td>
                                            <h5 className="d-block">{i18n.t('jt_channel.text')}</h5>
                                            <span className="d-block event-detail-text-sm">
                                                {eventDetails?.channel?.name ? eventDetails.channel.name : RenderNoDataAvailable()}</span>
                                        </td>

                                        {eventDetails.eventType.name === EVENT_TYPE_EMAIL && (
                                            <td>
                                                <h5 className="d-block">{i18n.t('jt_email_type.text')}</h5>
                                                <span className="d-block event-detail-text-sm">
                                                    <RenderFieldValue value={eventDetails.eventEmailType} fieldName="eventEmailType" restrictedFields={detailRestrictedFields} />
                                                </span>
                                            </td>
                                        )}
                                        {eventDetails.eventType.name === EVENT_TYPE_CHAT && (
                                            <td>
                                                <h5 className="d-block">{i18n.t('jt_chat_type.text')}</h5>
                                                <span className="d-block event-detail-text-sm">
                                                    <RenderFieldValue value={eventDetails.eventChatType} fieldName="eventChatType" restrictedFields={detailRestrictedFields} />
                                                </span>
                                            </td>
                                        )}
                                        {eventDetails.eventType.name === EVENT_TYPE_FEEDBACK && (
                                            <td>
                                                <h5 className="d-block">{i18n.t('jt_feedback_type.text')}</h5>
                                                <span className="d-block event-detail-text-sm">
                                                    {eventDetails.eventFeedbackType ? eventDetails.eventFeedbackType : i18n.t('jt_no_data_available.text')}
                                                </span>
                                            </td>
                                        )}
                                    </tr>

                                    <EventAgentCustomerNameBlock eventDetails={eventDetails} eventIndexDetails={eventIndexDetails} userData={userData} restrictedFields={detailRestrictedFields} />

                                </tbody>
                            </Table>
                        </div>
                    </Card>
                    <BaseModal
                        isOpen={changeLangModalOpen}
                        onClose={() => setChangeLangModalOpen(false)}
                        title={i18n.t('jt_change_language.text')}
                    >
                        <EventLangChangeForm
                            onDone={closeSettingsModal}
                            eventTypeId={eventDetails.eventType.id.toString()}
                        />
                    </BaseModal>
                </Col>
            )
        }

        // Chat Detail Block

        let textContents;

        let activeContentId;
        if (!loading && eventDetails) {
            if (eventDetails.eventTypeName === EVENT_TYPE_CHAT) {
                textContents = (
                    <Col>
                        {userData?.indexAnonymized && (
                            <Badge
                                pill
                                bg="outline-info"
                                className="anonymisation-badge mb-3"
                            >
                                {i18n.t('jt_anonymised_conversation.text')}
                            </Badge>
                        )}
                        <ChatDetailBlock
                            eventData={eventDetails as any}
                        />
                    </Col>
                );
            } else if (eventDetails.eventTypeName === EVENT_TYPE_EMAIL) {
                textContents = (
                    <Col>
                        {userData?.indexAnonymized && (
                            <Badge
                                pill
                                bg="outline-info"
                                className="anonymisation-badge mb-3"
                            >
                                {i18n.t('jt_anonymised_conversation.text')}
                            </Badge>
                        )}
                        <EmailDetailBlock
                            eventData={eventDetails as any}
                        />
                    </Col>
                );
            } else if (eventDetails.eventTypeName === EVENT_TYPE_FEEDBACK) {
                textContents = (
                    <Col>
                        {userData?.indexAnonymized && (
                            <Badge
                                pill
                                bg="outline-info"
                                className="anonymisation-badge mb-3"
                            >
                                {i18n.t('jt_anonymised_conversation.text')}
                            </Badge>
                        )}
                        <FeedbackDetailBlock
                            eventData={eventDetails as any}
                        />
                    </Col>
                );
            }
        }

        const NoteModal = ({show, onHide, eventId}: {show: boolean; onHide: () => void; eventId: number}) => (
            <BaseModal
                isOpen={show}
                onClose={onHide}
                title={modalMode === 'add' ? i18n.t('es_add_note.text') : i18n.t('es_edit_note.text')}
            >
                <AddNoteForm
                    key={selectedComment ? selectedComment.id : 'new-comment'}
                    onDone={onHide}
                    eventId={eventId}
                    existingComment={selectedComment || undefined}
                    mode={modalMode}
                    data-cy="note-modal"
                />
            </BaseModal>
        );

        const handleAddNote = () => {
            setModalMode('add');
            setSelectedComment(null);
            setCommentModalOpen(true);
        }

        const handleEditNote = (comment: any) => {
            setModalMode('edit');
            setSelectedComment(comment);
            setCommentModalOpen(true);
        }

        // Event Notes
        let eventCommentsList;
        if (loadingEventComments) {
            eventCommentsList = (<CellLoader fullHeight={true} />);
        } else if (eventComments.length) {
            eventCommentsList = eventComments.map((x, index) => (
                <div className="d-flex flex-row mb-3" key={index}>
                    <div className="w-100 mb-3">
                        <div className="d-flex flex-row align-self-stretch align-items-center justify-content-between">
                            <h5 className="m-0 event-detail-text-md">{x.user.userDisplayName || x.user.username}</h5>
                            {userData?.id === x.user.id && (
                                <Button
                                    variant="link"
                                    className="p-0 text-decoration-none shadow-none text-secondary"
                                    onClick={() => handleEditNote(x)}
                                    data-cy="note-edit-btn"
                                    aria-label={i18n.t('es_edit_note.text')}
                                >
                                    <Icon name="mdi-pencil-outline" className="mt-0" size='xs' />
                                </Button>
                            )}
                        </div>
                        <span className="text-muted event-detail-text-sm">
                            <FormattedDateTime
                                dateTime={x.createdOn}
                                showTime={false}
                                options={{setZone: false}}
                            />
                        </span>
                        <hr className="my-2 custom-separator" />
                        <div
                            className="comment-container event-detail-text-sm"
                            dangerouslySetInnerHTML={{__html: x.comment}}
                        />
                    </div>
                </div>
            ));
        } else {
            eventCommentsList = null;
        }

        // Notes
        const notesTab = (
            <>
                <Row className="mb-3">
                    <Col className="d-flex align-items-center">
                        <h4 className="mb-0 weight-300">{i18n.t('jt_notes.text')}</h4>
                    </Col>
                    <Col>
                        <Button
                            className="btn btn-info text-white float-right mb-0"
                            size='sm'
                            onClick={() => handleAddNote()}
                            data-cy="add-comment-button"
                        >
                            <span>
                                <Icon name="mdi-plus" className="mr-1" />
                                {i18n.t('jt_new_note.text')}
                            </span>
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <div className="pr-2 event-detail-notes-container">
                            {eventCommentsList
                                || <span className="text-muted">{i18n.t('jt_data_available.text')}</span>}
                        </div>
                    </Col>
                </Row>
                <NoteModal
                    show={commentModalOpen}
                    onHide={closeCommentModal}
                    eventId={Number(eventDetails?.id)}
                    data-cy="note-modal"
                />
            </>
        );

        const openConfirmFunction = (
            confirmMessage: string,
            url: string,
            functionName: string,
            displayName?: string,
        ) => {
            setConfirmAction({
                message: confirmMessage,
                url,
                functionName,
                displayName: displayName ?? functionName,
            });
            setConfirmModalOpen(true);
        };

        const closeConfirmModal = (): void => {
            setConfirmAction(null);
            setConfirmModalOpen(false);
            setIsExecuting(false);
        };

        const executeConfirmedAction = async (): Promise<void> => {
            if (!confirmAction) {return;}

            setIsExecuting(true);
            try {
                const response = await axios.post(confirmAction.url);
                if (confirmAction.functionName === 'Delete') {
                    const {getRootRedirectUrl} = await import('../../utils/rootUrlResolver');
                    navigate(getRootRedirectUrl({userData, menuStructure}));
                } else if (confirmAction.functionName === REPLACE_ORIGINAL_ACTION) {
                    showSuccess(i18n.t('jt_replace_original_successfully.text'));
                    closeConfirmModal();
                    window.location.reload();
                } else {
                    showSuccess(`${i18n.t('jt_function_name_started_successfully.text')} ${confirmAction.functionName}`);
                    closeConfirmModal();
                }
            } catch (error) {
                const errorMessage = extractErrorMessage(error, 'Unknown error');
                showError(`${confirmAction.displayName} ${i18n.t('jt_started_unsuccessfully.text')}, error: ${errorMessage}`);
                setIsExecuting(false);
            }
        };

        const insertToIndex = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_insert_to_index.text'),
                buildURL('eventInsertToIndex', {event_id: activeEventId}),
                i18n.t('jt_insert_to_index.text'),
            );
        };

        const getAnonymization = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_anonymisation.text'),
                buildURL('eventGetAnonymization', {event_id: activeEventId}),
                i18n.t('jt_anonymisation.text'),
            );
        };

        const getEvaluate = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_evaluate.text'),
                buildURL('eventEvaluate', {event_id: activeEventId}),
                i18n.t('jt_evaluate.text'),
            );
        };

        const postProcess = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_post_process.text'),
                buildURL('eventPostProcess', {event_id: activeEventId}),
                i18n.t('jt_post_process.text'),
            );
        };

        const ruleBasedActions = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_rule_based_actions.text'),
                buildURL('eventRuleBasedActions', {event_id: activeEventId}),
                i18n.t('jt_rule_based_actions.text'),
            );
        };

        const replaceOriginal = async () => {
            await openConfirmFunction(
                i18n.t('jt_are_you_sure_to_replace_original.text'),
                buildURL('eventReplaceOriginal', {event_id: activeEventId}),
                REPLACE_ORIGINAL_ACTION,
                i18n.t('jt_replace_original.text'),
            );
        };

        const deleteEvent = async () => {
            await openConfirmFunction(
                i18n.t('jt_confirm_delete_event.text'),
                buildURL('eventDelete', {event_id: activeEventId}),
                i18n.t('jt_delete.text'),
            );
        };

        const eventActionModal = () => {
            const footer = (
                <Button
                    variant="info"
                    data-cy="copy-index-data-btn"
                    onClick={() => {
                        navigator.clipboard.writeText(actionModalContent).then(
                            () => {
                                showSuccess(i18n.t('content_copied_to_clipboard.text'));
                            },
                            (err) => {
                                console.error('Failed to copy: ', err);
                                showError(i18n.t('failed_to_copy_content.text'));
                            }
                        );
                    }}
                >
                    {i18n.t('jt_copy.text')}
                </Button>
            );

            return (
                <BaseModal
                    isOpen={actionModalOpen}
                    onClose={() => setActionModalOpen(false)}
                    title={actionModalTitle}
                    size="xl"
                    footer={footer}
                >
                    <div className="event-detail-action-modal-content">
                        <Card className="event-detail-action-modal-card">
                            <pre className="event-detail-action-modal-pre">
                                {actionModalContent}
                            </pre>
                        </Card>
                    </div>
                </BaseModal>
            );
        };

        const getTabTitle = (eventTypeName: string) => {
            switch (eventTypeName) {
                case EVENT_TYPE_CHAT:
                    return i18n.t('jt_chat_details.text');
                case EVENT_TYPE_FEEDBACK:
                    return i18n.t('jt_feedback_details.text');
                case EVENT_TYPE_EMAIL:
                    return i18n.t('jt_email_details.text');
                default:
                    return '';
            }
        };

        const generateInsightText = (type: string, channelCompAvg: number, channelComp: number, percentageSameValue: number | undefined, percentageSameTopic: number | undefined) => {
            if (type === 'numerical_descriptor') {
                const prefix = channelComp > 0 ? '+' : '';
                return (
                    <>
                        <label className="event-detail-text-lg">
                            {i18n.t('jt_channel_average.text')}
                            <Tooltip
                                content={i18n.t('jt_shows_how.text')}
                                placement="top"
                            >
                                <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info event-detail-card-clickable" />
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{prefix}{channelComp}%</span>
                            <span>{'  '}({channelCompAvg})</span>
                        </label>
                    </>
                )
            } else if (type === 'list_field' && percentageSameValue !== undefined) {
                return (
                    <>
                        <label className="event-detail-text-lg">
                            {i18n.t('jt_channel_similarity.text')}
                            <Tooltip
                                content={i18n.t('jt_represents_the_value.text')}
                                placement="top"
                            >
                                <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info event-detail-card-clickable" />
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{percentageSameValue.toFixed(2)}%</span>
                        </label>
                    </>
                )
            } else if ((type === 'selection_field' || type === 'computed_classification') && percentageSameTopic !== undefined) {
                return (
                    <>
                        <label className="event-detail-text-lg">
                            {i18n.t('jt_channel_similarity.text')}
                            <Tooltip
                                content={i18n.t('jt_represents_the_category.text')}
                                placement="top"
                            >
                                <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info event-detail-card-clickable" />
                            </Tooltip>
                        </label>
                        <label className="event-detail-text-xl">
                            <span>{percentageSameTopic.toFixed(2)}%</span>
                        </label>
                    </>
                )
            }
            return i18n.t('jt_no_data_available.text');
        };

        const ModuleCards = ({data, evaluations, userData}: {data: any; evaluations: any[]; userData: any}) => {
            const [openStates, setOpenStates] = useState<{[key: string]: boolean}>(
                Object.keys(data).reduce((acc, key, index) => {
                    acc[key] = index === 0; // Set the first card (index 0) to open, others closed
                    return acc;
                }, {} as {[key: string]: boolean})
            );

            const getModuleNotAvailableText = (moduleKey: string, information: any) => {
                if (moduleKey === 'addon-quality-score') {
                    return i18n.t('jt_automatic_quality_score_not_included.text');
                }

                if (moduleKey === 'addon-call-metric-score') {
                    return i18n.t('jt_call_metric_score_not_included.text');
                }

                if (information?.text_if_module_not_available) {
                    return information.text_if_module_not_available;
                }

                return i18n.t('jt_feature_not_included.text', {
                    business_name: information?.business_name,
                });
            };

            return (
                <Col md={4}>
                    {/* Dynamically render each module, excluding those without "overall" */}
                    {Object.entries(data)
                        .filter(([moduleKey, moduleData]) => (moduleData as any).overall) // Exclude modules without "overall"
                        .map(([moduleKey, moduleData]) => {
                            const typedModuleData = moduleData as any;
                            const isModuleAvailable = isModuleEnabled(userData, moduleKey);
                            const isOpen = openStates[moduleKey];
                            const toggleCard = () =>
                                setOpenStates((prev) => ({...prev, [moduleKey]: !prev[moduleKey]}));

                            // Special Case for 'insights' Module
                            if (moduleKey === 'insights') {
                                const topN = typedModuleData.overall?.top_n || 3;
                                // Process evaluations before sorting & filtering
                                const processedEvaluations = useMemo(() => {
                                    return evaluations.filter((x) => x.type !== 'list_field').flatMap((x) => {
                                        let channelComp = 0;
                                        let channelCompAvg = 0;

                                        if (x.channelComp?.avg) {
                                            channelComp = Number(((x.value / x.channelComp.avg) * 100 - 100).toFixed(2));
                                            channelCompAvg = x.channelComp.avg.toFixed(2);
                                        }

                                        let eventTypeComp = 0;
                                        let eventTypeCompAvg = 0;
                                        if (x.eventTypeComp?.avg) {
                                            eventTypeComp = Number(((x.value / x.eventTypeComp.avg) * 100 - 100).toFixed(2));
                                            eventTypeCompAvg = x.eventTypeComp.avg.toFixed(2);
                                        }

                                        const insight = generateInsightText(
                                            x.type,
                                            channelCompAvg,
                                            channelComp,
                                            x.percentage_same_value,
                                            x.percentage_same_topic
                                        );

                                        return [
                                            {
                                                id: x.id,
                                                label: x.label,
                                                value: x.value,
                                                channelAverage: channelCompAvg,
                                                channelComp: channelComp,
                                                eventTypeAverage: eventTypeCompAvg,
                                                eventTypeComp: eventTypeComp,
                                                description: x.description,
                                                insightText: insight,
                                                priority: x.priority || false,
                                            },
                                        ];
                                    });
                                }, [evaluations]);

                                // Sort and prioritize top evaluations
                                const topEvaluations = useMemo(() => {
                                    if (!processedEvaluations.length) {return [];}

                                    // Sort by highest absolute percentage change (`channelComp`)
                                    const sortedByChange = [...processedEvaluations].sort(
                                        (a, b) => Math.abs(b.channelComp) - Math.abs(a.channelComp)
                                    );

                                    // Take top 3 absolute changes, whether positive or negative
                                    const topChanges = sortedByChange.slice(0, 3);

                                    // Fill the rest with remaining evaluations to reach `topN` total
                                    const remainingEvaluations = sortedByChange.slice(3);

                                    return [...topChanges, ...remainingEvaluations].slice(0, topN);
                                }, [processedEvaluations, topN]);

                                return (
                                    <Card
                                        key={moduleKey}
                                        className="mb-3 event-detail-insights-card"
                                    >
                                        <Card.Header className="rounded bg-white event-detail-card-header-no-border">
                                            <div
                                                onClick={toggleCard}
                                                className="p-2 w-100 d-flex justify-content-between align-items-center text-dark event-detail-card-clickable"
                                            >
                                                <div className="d-flex align-items-center">
                                                    <h4 className="m-0">{typedModuleData.information?.business_name}</h4>
                                                    <Icon name={`mdi-chevron-${isOpen ? 'up' : 'down'}`} size="18px" className="ml-2" />
                                                </div>
                                                <h4 className="m-0 d-flex align-items-center">
                                                    Top {topEvaluations.length < topN && topEvaluations.length > 3 ? topEvaluations.length : topN}
                                                    <Tooltip
                                                        content={i18n.t('jt_insight_tooltip.text', {
                                                            days: appConfig.EVALUATION_TYPE_BEFORE_X_DAYS_THRESHOLD
                                                        })}
                                                        placement="top"
                                                    >
                                                        <span
                                                            className="ml-1 event-detail-card-clickable"
                                                        >
                                                            <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                                        </span>
                                                    </Tooltip>
                                                </h4>
                                            </div>
                                        </Card.Header>

                                        <Collapse in={isOpen}>
                                            <Card.Body className="pt-0">
                                                <hr className="mt-0" />
                                                {loadingEvaluations ? (
                                                    <p>{i18n.t('jt_loading_insights.text')}</p>
                                                ) : topEvaluations.length > 0 ? (
                                                    topEvaluations.map((evaluation) => {
                                                        return (
                                                            <div key={evaluation.id}>
                                                                <div
                                                                    className="d-flex justify-content-between align-items-center mt-2">
                                                                    {/* Label */}
                                                                    <label className="m-0 event-detail-label-gray">
                                                                        {evaluation.label}
                                                                        <Tooltip
                                                                            content={evaluation.description ? evaluation.description.replace(/<[^>]+>/g, '') :
                                                                                i18n.t('event_det_sum_no_desc.text')}
                                                                            placement="top"
                                                                        >
                                                                            <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info event-detail-card-clickable" />
                                                                        </Tooltip>
                                                                    </label>

                                                                    <h6 className="m-0 d-flex align-items-center">
                                                                        {/* Value/insights text Display */}
                                                                        <span>{evaluation.value}</span>
                                                                    </h6>
                                                                </div>
                                                                <div
                                                                    className="d-flex justify-content-between align-items-center mt-1">
                                                                    {evaluation.insightText}
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <p>{i18n.t('jt_data_available.text')}</p>
                                                )}
                                            </Card.Body>
                                        </Collapse>
                                    </Card>
                                );
                            }

                            return (
                                <Card
                                    key={moduleKey}
                                    className="mb-3 event-detail-module-card"
                                    data-border-color={typedModuleData.overall.value ? getScoreColor(typedModuleData.overall.value * typedModuleData.overall.multiplier, typedModuleData) : '#CCCCCC'}
                                >
                                    {/* Card Header */}
                                    <Card.Header className="rounded bg-white event-detail-card-header-no-border">
                                        <div
                                            onClick={toggleCard}
                                            className="p-2 w-100 d-flex justify-content-between align-items-center text-dark event-detail-card-clickable"
                                        >
                                            <div className="d-flex align-items-center">
                                                <h4 className="m-0">{typedModuleData.overall.label || i18n.t('overall_score.text')}</h4>
                                                <Icon name={`mdi-chevron-${isOpen ? 'up' : 'down'}`} size="18px" className="ml-2" />
                                            </div>
                                            <h4
                                                className="m-0 event-detail-module-score"
                                                data-score-color={typedModuleData.overall.value != null ? getScoreColor(typedModuleData.overall.value * typedModuleData.overall.multiplier, typedModuleData) : 'inherit'}
                                            >
                                                {typedModuleData.overall.value != null
                                                    ? `${typedModuleData.overall.value * typedModuleData.overall.multiplier}%`
                                                    : ''}
                                            </h4>
                                        </div>
                                    </Card.Header>

                                    {/* Card Body */}
                                    <Collapse in={isOpen}>
                                        <Card.Body className="pt-0">
                                            <hr className="mt-0" />
                                            {isModuleAvailable ? (
                                                <>
                                                    {/* Render keys */}
                                                    {typedModuleData.keys?.map((keyItem: any) => (
                                                        <div
                                                            key={keyItem.key}
                                                            className="d-flex justify-content-between align-items-center mt-2"
                                                        >
                                                            <div className="d-inline-flex align-items-center">
                                                                <label className="m-0">{keyItem.label || '-'}</label>
                                                                <Tooltip
                                                                    content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                                        i18n.t('event_det_sum_no_desc.text')}
                                                                    placement="top"
                                                                >
                                                                    <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info" />
                                                                </Tooltip>
                                                            </div>
                                                            <h6 className="m-0">
                                                                {keyItem.value != null
                                                                    ? `${keyItem.value}${keyItem.max_value ? `/${keyItem.max_value}` : ''}`
                                                                    : `-${keyItem.max_value ? `/${keyItem.max_value}` : ''}`}
                                                            </h6>

                                                        </div>
                                                    ))}

                                                    {/* Render additional information */}
                                                    {typedModuleData.additional?.length > 0 && <hr />}
                                                    {typedModuleData.additional?.map((additionalItem: any, index: number) => (
                                                        <div key={additionalItem.key} className="mt-2">
                                                            <h5 className="d-flex align-items-center">{additionalItem.label || '-'}
                                                                <Tooltip
                                                                    content={additionalItem.description ? additionalItem.description.replace(/<[^>]+>/g, '') :
                                                                        i18n.t('tt_no_tooltip.text')}
                                                                    placement="top"
                                                                >
                                                                    <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info" />
                                                                </Tooltip>
                                                            </h5>
                                                            <label className="m-0">
                                                                {additionalItem.value || '-'}
                                                            </label>
                                                            {index < typedModuleData.additional.length - 1 && <hr />}
                                                        </div>
                                                    ))}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="overlay-text">
                                                        {getModuleNotAvailableText(moduleKey, typedModuleData.information)}
                                                    </span>

                                                    <div className="blur-container">
                                                        {/* Ensure at least 5 key items */}
                                                        {[
                                                            ...(typedModuleData.keys || []),
                                                            ...Array.from(
                                                                {length: Math.max(0, 5 - (typedModuleData.keys?.length || 0))},
                                                                (_, index) => ({
                                                                    key: `placeholder-${index}`,
                                                                    label: '-',
                                                                    max_value: '-',
                                                                    description: i18n.t('jt_no_data_available.text'),
                                                                    isPlaceholder: true, // Identify placeholders
                                                                })
                                                            ),
                                                        ].map((keyItem) => (
                                                            <div
                                                                key={keyItem.key}
                                                                className={`d-flex justify-content-between align-items-center mt-2 ${keyItem.isPlaceholder ? 'opacity-50' : '' // Style placeholders with lower opacity
                                                                    }`}
                                                            >
                                                                <div className="d-inline-flex align-items-center">
                                                                    <label className="m-0">{keyItem.label}</label>
                                                                    {!keyItem.isPlaceholder && (
                                                                        <Tooltip
                                                                            content={keyItem.description ? keyItem.description.replace(/<[^>]+>/g, '') :
                                                                                i18n.t('event_det_sum_no_desc.text')}
                                                                            placement="top"
                                                                        >
                                                                            <Icon name="mdi-information-outline" size="xs" className="ml-1 p-0 text-info" />
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                                <h6 className="m-0">{`-/ ${keyItem.max_value}`}</h6>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </Card.Body>
                                    </Collapse>
                                </Card>
                            );
                        })}
                </Col>
            );
        };

        const getScoreColor = (score: number | null, config: any) => {
            if (score == null) {
                return '#CCCCCC';
            }

            const scoreString = score.toString();
            const scoreValue = parseInt(scoreString.replace('%', ''), 10);

            // Get thresholds from config (default to 80 and 40 if not specified)
            const highThreshold = config?.overall?.high_threshold ?? 80;
            const lowThreshold = config?.overall?.low_threshold ?? 40;

            if (scoreValue >= highThreshold) {return '#55ce63';}
            if (scoreValue <= lowThreshold) {return 'red';}
            return '#EFAD4F';
        };

        return (
            <>
                {/*Event Header*/}
                <Row>
                    <Col md={12}>
                        <Card className="mb-3">
                            <Card.Body>
                                {eventDetails ? (
                                    <Row>
                                        <Col>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <EventHeader
                                                    eventType={eventDetails.eventTypeName}
                                                    eventId={String(eventDetails.id)}
                                                    channelName={eventDetails.channel?.name || ''}
                                                    dateTime={eventDetails.datetime}
                                                    eventAgent={Array.isArray(eventAgent) ? eventAgent[0]?.agentName || '' : (typeof eventAgent === 'object' ? eventAgent?.agentName || '' : '')}
                                                    eventCustomer={Array.isArray(eventCustomer) ? eventCustomer[0]?.customerName || '' : (typeof eventCustomer === 'object' ? eventCustomer?.customerName || '' : '')}
                                                    restrictedFields={detailRestrictedFields}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                ) : RenderNoDataAvailable()}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/*Tabs*/}
                <Row>
                    <Col>
                        <div className="card mb-3">
                            <div className="card-body">
                                <Tab.Container activeKey={activeTab} onSelect={handleTabSelect as any}>
                                    <div className="d-flex align-items-center">
                                        <Nav variant="tabs" className="d-flex w-100 mb-3 justify-content-between">
                                            <div className="d-flex flex-wrap">
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs" eventKey="call-details">
                                                        {getTabTitle(eventDetails?.eventTypeName || '')}
                                                    </Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="conversation">{i18n.t('jt_conversation.text')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="evaluations">{i18n.t('jt_evaluations.text')}</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item>
                                                    <Nav.Link className="custom-tabs"
                                                        eventKey="notes">{i18n.t('jt_notes.text')}</Nav.Link>
                                                </Nav.Item>
                                                {
                                                    canAccessAudit ? (
                                                        <Nav.Item>
                                                            <Nav.Link className="custom-tabs"
                                                                eventKey="audit">{i18n.t('jt_audit.text')}</Nav.Link>
                                                        </Nav.Item>
                                                    ) : null
                                                }
                                            </div>

                                            {(() => {
                                                const hasViewItems = (eventDetails?.eventType?.name === EVENT_TYPE_CHAT && hasPermission('fs.event.index_data.read', userData?.permissions, true))
                                                    || hasPermission('fs.event.index_data.read', userData?.permissions, true);
                                                const hasActionItems = hasPermission('fs.event.insert_to_index.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.anonymisation.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.evaluation.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.post_process.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.rule_based_action.execute', userData?.permissions, true)
                                                    || hasPermission('fs.event.delete', userData?.permissions, true);
                                                const hasAnyItems = hasViewItems || hasActionItems;

                                                return (
                                                    <Nav.Item>
                                                        <Tooltip content={hasAnyItems ? i18n.t('jt_actions.text') : i18n.t('tt_no_actions_available.text')} placement="top">
                                                            {hasAnyItems ? (
                                                                <Dropdown>
                                                                    <Dropdown.Toggle
                                                                        as={Button}
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        icon="mdi-dots-horizontal"
                                                                        className="dropdown-toggle-no-caret"
                                                                        data-cy="event-actions-toggle"
                                                                        id="settings-dropdown-1"
                                                                    />

                                                                    <Dropdown.Menu align="end">
                                                                        {hasViewItems && (
                                                                            <Dropdown.Header
                                                                                className="custom-dropdown-header text-muted">{i18n.t('jt_view.text')}</Dropdown.Header>
                                                                        )}
                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CHAT && hasPermission('fs.event.index_data.read', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={() => {
                                                                                        setActionModalOpen(true);
                                                                                        setActionModalTitle('Payload');
                                                                                        setActionModalContent(JSON.stringify(eventDetails?.eventChatChat, null, 2));
                                                                                    }}
                                                                                    className="d-inline-block"
                                                                                >
                                                                                    {i18n.t('jt_payload.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventDetails?.eventType?.name === EVENT_TYPE_CHAT && hasPermission('fs.event.index_data.read', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={() => {
                                                                                        setActionModalOpen(true);
                                                                                        setActionModalTitle('Payload original');
                                                                                        setActionModalContent(JSON.stringify(eventDetails?.eventChatChatOriginal, null, 2));
                                                                                    }}
                                                                                    className="d-inline-block"
                                                                                >
                                                                                    {i18n.t('jt_payload_original.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.index_data.read', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={() => {
                                                                                        setActionModalOpen(true);
                                                                                        setActionModalTitle('Index data');
                                                                                        setActionModalContent(JSON.stringify(eventIndexDetails, null, 2));
                                                                                    }}
                                                                                    className="d-inline-block"
                                                                                >
                                                                                    {i18n.t('jt_index_data.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasActionItems && (
                                                                            <Dropdown.Header
                                                                                className="custom-dropdown-header text-muted">{i18n.t('jt_action.text')}</Dropdown.Header>
                                                                        )}
                                                                        {hasPermission('fs.event.insert_to_index.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={insertToIndex}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-without-player-jtinsertindex1-text-btn">
                                                                                    {i18n.t('jt_insert_index.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.anonymisation.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={getAnonymization}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-without-player-jtanonymise1-text-btn">
                                                                                    {i18n.t('jt_anonymise.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.evaluation.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={getEvaluate}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-without-player-jtevaluate1-text-btn">
                                                                                    {i18n.t('jt_evaluate.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.post_process.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={postProcess}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-without-player-jtpostprocess1-text-btn">
                                                                                    {i18n.t('jt_post_process.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.rule_based_action.execute', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={ruleBasedActions}
                                                                                    className="d-inline-block"
                                                                                    data-cy="event-detail-block-without-player-jtforceaction1-text-btn">
                                                                                    {i18n.t('jt_force_action.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {hasPermission('fs.event.delete', userData?.permissions, true)
                                                                            ? (
                                                                                <Dropdown.Item
                                                                                    onClick={deleteEvent}
                                                                                    className="d-inline-block text-danger"
                                                                                    data-cy="event-detail-block-without-player-jtdelete21-text-btn">
                                                                                    {i18n.t('jt_delete.text')}
                                                                                </Dropdown.Item>
                                                                            ) : null}

                                                                        {eventActionModal()}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                            ) : (
                                                                <span className="d-inline-block">
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        size="sm"
                                                                        icon="mdi-dots-horizontal"
                                                                        className="dropdown-toggle-no-caret"
                                                                        data-cy="event-actions-toggle"
                                                                        disabled
                                                                        style={{pointerEvents: 'none'}}
                                                                    />
                                                                </span>
                                                            )}
                                                        </Tooltip>
                                                    </Nav.Item>
                                                );
                                            })()}
                                        </Nav>
                                    </div>


                                    <Tab.Content>
                                        <Tab.Pane eventKey="call-details">
                                            <Row id="event-detail-display">
                                                {eventSummaryDisplay}
                                                {eventInfoDisplay}
                                            </Row>
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="conversation">
                                            <Row id="conversation-detail-display">
                                                {textContents}
                                            </Row>
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="evaluations">
                                            <EventEvaluationsTab eventId={String(activeEventId)}
                                                isEvaluationsTabClicked={isEvaluationsTabClicked} />
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="notes">
                                            {notesTab}
                                        </Tab.Pane>
                                        <Tab.Pane eventKey="audit">
                                            {eventDetails && <AuditTab eventDetails={eventDetails as any} userData={userData}
                                                isAuditTabClicked={isAuditTabClicked} />}
                                        </Tab.Pane>
                                    </Tab.Content>
                                </Tab.Container>
                            </div>
                        </div>
                    </Col>

                    {userData && data && <ModuleCards data={data} evaluations={evaluations} userData={userData} />}
                </Row>
                <ModalAlert
                    isOpen={confirmModalOpen}
                    onClose={closeConfirmModal}
                    variant="warning"
                    title={i18n.t('jt_confirm_action.text')}
                    message={confirmAction?.message || ''}
                    actions={[
                        {
                            label: i18n.t('jt_confirm.text'),
                            onClick: executeConfirmedAction,
                            variant: 'primary',
                            loading: isExecuting
                        }
                    ]}
                    dismissible={!isExecuting}
                />
            </>
        )
    }
);

export default EventDetailBlockWithoutPlayer;
